/**
 * Protected Route Component
 *
 * Wrapper component that requires authentication to access child routes.
 * Redirects to login page if user is not authenticated.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/state/auth-context';
import type { ReactNode } from 'react';

/**
 * Protected Route Props
 */
interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Protected Route Component
 *
 * Checks if user is authenticated before rendering children.
 * Shows loading state while checking authentication.
 * Redirects to login if not authenticated, preserving intended destination.
 *
 * @param props - Component props
 * @returns Protected content or redirect
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { state, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (state.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-sm text-muted-foreground">Loading authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving intended destination
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // User is authenticated - render protected content
  return <>{children}</>;
}
